import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Leaf,
  ArrowLeft,
  LogOut,
  MessageSquare,
  Mail,
  Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const auth = sessionStorage.getItem("adminAuth");
  if (!auth) return null;
  return { Authorization: `Basic ${auth}` };
};

export const AdminMessagesPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const headers = getAuthHeaders();

  useEffect(() => {
    if (!headers) {
      navigate("/admin");
      return;
    }
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/messages`, { headers });
      setMessages(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem("adminAuth");
        navigate("/admin");
      } else {
        toast.error("Failed to load messages");
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/admin/messages/${messageId}/read`, null, { headers });
      fetchMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage({ ...selectedMessage, is_read: true });
      }
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-deep-moss font-serif text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone-cream animate-fade-in">
      {/* Header */}
      <header className="bg-deep-moss text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Leaf className="w-6 h-6" />
              <span className="font-serif text-xl font-semibold">WEH Admin</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 font-sans text-sm text-white/80 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/admin/dashboard"
            data-testid="back-to-dashboard"
            className="text-stone-500 hover:text-deep-moss"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-serif text-3xl font-semibold text-stone-900">Messages</h1>
        </div>

        {messages.length === 0 ? (
          <div className="bg-white p-12 text-center border border-stone-200">
            <MessageSquare className="w-12 h-12 mx-auto text-stone-300 mb-4" />
            <p className="font-sans text-stone-500">No messages yet</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Messages List */}
            <div className="lg:col-span-2 bg-white border border-stone-200 divide-y divide-stone-200">
              {messages.map((message) => (
                <div
                  key={message.id}
                  onClick={() => setSelectedMessage(message)}
                  data-testid={`message-row-${message.id}`}
                  className={`p-4 cursor-pointer hover:bg-stone-50 transition-colors ${
                    selectedMessage?.id === message.id ? "bg-bone-cream" : ""
                  } ${!message.is_read ? "border-l-4 border-l-terracotta" : ""}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <p className="font-sans text-sm font-medium text-stone-900">{message.name}</p>
                      {!message.is_read && (
                        <span className="w-2 h-2 rounded-full bg-terracotta"></span>
                      )}
                    </div>
                    <span className="font-sans text-xs text-stone-500">
                      {format(new Date(message.created_at), "MMM d")}
                    </span>
                  </div>
                  <p className="font-sans text-sm text-stone-900 mb-1">{message.subject}</p>
                  <p className="font-sans text-sm text-stone-500 line-clamp-1">{message.message}</p>
                </div>
              ))}
            </div>

            {/* Message Details */}
            <div className="lg:col-span-1">
              {selectedMessage ? (
                <div className="bg-white p-6 border border-stone-200 sticky top-24 animate-fade-in">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="font-serif text-lg font-semibold text-stone-900">
                      Message Details
                    </h3>
                    {!selectedMessage.is_read && (
                      <button
                        onClick={() => markAsRead(selectedMessage.id)}
                        data-testid="mark-read-btn"
                        className="flex items-center gap-1 text-xs text-deep-moss hover:underline"
                      >
                        <Check className="w-3 h-3" />
                        Mark as read
                      </button>
                    )}
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <p className="font-sans text-xs text-stone-500 uppercase">From</p>
                      <p className="font-sans text-sm text-stone-900">{selectedMessage.name}</p>
                    </div>
                    <div>
                      <p className="font-sans text-xs text-stone-500 uppercase">Email</p>
                      <a
                        href={`mailto:${selectedMessage.email}`}
                        className="font-sans text-sm text-deep-moss hover:underline"
                      >
                        {selectedMessage.email}
                      </a>
                    </div>
                    <div>
                      <p className="font-sans text-xs text-stone-500 uppercase">Subject</p>
                      <p className="font-sans text-sm text-stone-900">{selectedMessage.subject}</p>
                    </div>
                    <div>
                      <p className="font-sans text-xs text-stone-500 uppercase">Date</p>
                      <p className="font-sans text-sm text-stone-900">
                        {format(new Date(selectedMessage.created_at), "MMMM d, yyyy 'at' h:mm a")}
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-stone-200 pt-4">
                    <p className="font-sans text-xs text-stone-500 uppercase mb-2">Message</p>
                    <p className="font-sans text-sm text-stone-900 whitespace-pre-wrap">
                      {selectedMessage.message}
                    </p>
                  </div>

                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                    data-testid="reply-btn"
                    className="flex items-center justify-center gap-2 w-full mt-6 bg-deep-moss hover:bg-deep-moss/90 text-white rounded-full px-4 py-2 font-sans text-sm font-medium"
                  >
                    <Mail className="w-4 h-4" />
                    Reply via Email
                  </a>
                </div>
              ) : (
                <div className="bg-white p-12 text-center border border-stone-200">
                  <Mail className="w-8 h-8 mx-auto text-stone-300 mb-2" />
                  <p className="font-sans text-sm text-stone-500">
                    Select a message to view details
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
